import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import sebearLogo from '../assets/images/Sebear-logo.svg';

const Login = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useWallet();
  const { login, user, refreshUser } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    if (user && isConnected && address) {
      setStatus('User found! Redirecting...');
      setTimeout(() => {
        redirectToDashboard(user);
      }, 1500);
    }
  }, [user, isConnected, address]);

  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        redirectToDashboard(user);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    if (isConnected && address && !user) {
      setStatus('Wallet connected! Click below to complete login.');
    } else if (!isConnected) {
      setStatus('');
      setError(null);
    }
  }, [isConnected, address, user]);

  const redirectToDashboard = (userData: any) => {
    if (userData.role === 'admin') {
      navigate('/dashboard/investor');
    } else if (userData.role === 'issuer') {
      navigate('/dashboard/issuer');
    } else {
      navigate('/dashboard/investor');
    }
  };

  const handleLogin = async () => {
    if (!address) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError(null);
    setStatus('Processing login...');

    try {
      await login(address);
      setStatus('Login successful! Redirecting...');

      setTimeout(() => {
        refreshUser(address).then((refreshedUser) => {
          if (refreshedUser) {
            redirectToDashboard(refreshedUser);
          }
        });
      }, 1500);

    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed. Please try again.');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return { label: 'Administrator', color: '#F59E0B' };
      case 'issuer':
        return { label: 'Asset Issuer', color: '#10B981' };
      default:
        return { label: 'Investor', color: '#3B82F6' };
    }
  };

  // Success state
  if (user) {
    const roleBadge = getRoleBadge(user.role);
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card
              sx={{
                background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 6, textAlign: 'center' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={sebearLogo}
                    alt="Sebear RWA"
                    style={{
                      height: '50px',
                      width: 'auto',
                      filter: isDark
                        ? 'none'
                        : 'invert(37%) sepia(93%) saturate(1352%) hue-rotate(196deg) brightness(97%) contrast(101%)',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.15)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 32, color: '#10B981' }} />
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Welcome Back!
                </Typography>

                <Chip
                  label={roleBadge.label}
                  size="small"
                  sx={{
                    mb: 3,
                    background: `${roleBadge.color}20`,
                    border: `1px solid ${roleBadge.color}40`,
                    color: roleBadge.color,
                    fontWeight: 600,
                  }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Redirecting to your dashboard...
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {address?.slice(0, 10)}...{address?.slice(-8)}
                  </Typography>
                </Box>

                <CircularProgress size={20} sx={{ mt: 3, color: '#3B82F6' }} />
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card
            sx={{
              background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              {/* Logo */}
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={sebearLogo}
                  alt="Sebear RWA"
                  style={{
                    height: '60px',
                    width: 'auto',
                    filter: isDark
                      ? 'none'
                      : 'invert(37%) sepia(93%) saturate(1352%) hue-rotate(196deg) brightness(97%) contrast(101%)',
                  }}
                />
              </Box>

              {/* Title */}
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Sign In
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Connect your wallet to access the RWA platform
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Status Alert */}
              {status && !error && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    background: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.08)',
                    border: `1px solid ${isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.15)'}`,
                  }}
                >
                  {status}
                </Alert>
              )}

              {/* Connect Wallet Step */}
              {!isConnected ? (
                <Box>
                  <Box
                    sx={{
                      mb: 4,
                      p: 3,
                      borderRadius: 2,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                      border: `1px dashed ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Step 1: Connect your Web3 wallet
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ConnectButton />
                  </Box>
                </Box>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Connected State */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 3,
                      borderRadius: 2,
                      background: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
                      border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <CheckIcon sx={{ fontSize: '1rem', color: '#10B981' }} />
                      <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                        Wallet Connected
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {address?.slice(0, 8)}...{address?.slice(-6)}
                    </Typography>
                  </Box>

                  {/* Step 2 */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Step 2: Complete authentication to access the platform
                  </Typography>

                  {/* Login Button */}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleLogin}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {loading ? 'Authenticating...' : 'Complete Login'}
                  </Button>

                  {/* Change Wallet Link */}
                  <Box sx={{ mt: 3 }}>
                    <ConnectButton.Custom>
                      {({ openConnectModal }) => (
                        <Button
                          variant="text"
                          size="small"
                          onClick={openConnectModal}
                          sx={{ color: 'text.secondary', textTransform: 'none' }}
                        >
                          Use a different wallet
                        </Button>
                      )}
                    </ConnectButton.Custom>
                  </Box>
                </motion.div>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}` }}>
                <Typography variant="caption" color="text.secondary">
                  By connecting, you agree to our Terms of Service and Privacy Policy
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Login;
